// Central SEO helper. Host follows the live demo domain and can be handed
// to the client by rebuilding with a new canonical host. No UI changes here.
export const DEFAULT_SITE_URL = 'https://antitraffickingmap.online';

export function getSiteUrl(): string {
  if (typeof window !== 'undefined' && window.location?.origin) {
    const host = window.location.hostname;
    // Keep admin and localhost behaviour safe: admin stays noindexed and
    // localhost keeps its own origin so local testing never leaks canonicals.
    if (host === 'dash.antitraffickingmap.online') return `https://${host}`;
    if (host.includes('localhost') || host.includes('127.0.0.1')) return window.location.origin;
    return DEFAULT_SITE_URL;
  }
  return DEFAULT_SITE_URL;
}

export type SupportedLang = 'ar' | 'fr' | 'en';

type RouteMeta = { title: Record<SupportedLang, string>; description: Record<SupportedLang, string> };

const SUFFIX: Record<SupportedLang, string> = {
  fr: 'Plateforme de soutien CNCLT Maroc',
  ar: 'منصة الدعم، اللجنة الوطنية لمكافحة الاتجار بالبشر',
  en: 'CNCLT Victim Support Platform Morocco',
};

export const ROUTE_META: Record<string, RouteMeta> = {
  '/': {
    title: {
      fr: `Accueil | ${SUFFIX.fr}`,
      ar: `الرئيسية | ${SUFFIX.ar}`,
      en: `Home | ${SUFFIX.en}`,
    },
    description: {
      fr: "Comprendre la traite des êtres humains, orienter victimes, témoins et déclarants vers l'aide, faciliter le signalement et l'accès à la protection au Maroc.",
      ar: 'تهدف هذه المنصة إلى التعريف بظاهرة الاتجار بالبشر وتوجيه الضحايا والشهود والمبلغين نحو المساعدة المناسبة وتسهيل التبليغ والوصول إلى خدمات الحماية والدعم.',
      en: 'Understand human trafficking and guide victims, witnesses and reporters to help, reporting and protection services in Morocco.',
    },
  },
  '/services': {
    title: { fr: `Carte des services | ${SUFFIX.fr}`, ar: `خريطة الخدمات | ${SUFFIX.ar}`, en: `Services map | ${SUFFIX.en}` },
    description: {
      fr: 'Trouvez les services de protection, hébergement, aide médicale, juridique et accompagnement près de chez vous.',
      ar: 'اعثروا على خدمات الحماية والإيواء والمساعدة الطبية والقانونية والمواكبة بالقرب منكم.',
      en: 'Find protection, shelter, medical, legal and support services near you.',
    },
  },
  '/all-services': {
    title: { fr: `Tous les services | ${SUFFIX.fr}`, ar: `جميع الخدمات | ${SUFFIX.ar}`, en: `All services | ${SUFFIX.en}` },
    description: {
      fr: 'Annuaire des acteurs de protection et de soutien aux victimes de la traite au Maroc.',
      ar: 'دليل الفاعلين في مجال حماية ضحايا الاتجار بالبشر ومواكبتهم في المغرب.',
      en: 'Directory of victim protection and support actors in Morocco.',
    },
  },
  '/interactive-map': {
    title: { fr: `Carte interactive | ${SUFFIX.fr}`, ar: `الخريطة التفاعلية | ${SUFFIX.ar}`, en: `Interactive map | ${SUFFIX.en}` },
    description: {
      fr: 'Explorez la carte interactive des services disponibles par ville au Maroc.',
      ar: 'استكشفوا الخريطة التفاعلية للخدمات المتوفرة حسب المدينة في المغرب.',
      en: 'Explore the interactive map of available services by city in Morocco.',
    },
  },
  '/about': {
    title: { fr: `À propos | ${SUFFIX.fr}`, ar: `حول المنصة | ${SUFFIX.ar}`, en: `About | ${SUFFIX.en}` },
    description: {
      fr: "La plateforme de soutien lancée par la Commission nationale de coordination de la lutte contre la traite des êtres humains.",
      ar: 'منصة الدعم التي أطلقتها اللجنة الوطنية للتنسيق في مجال مكافحة الاتجار بالبشر.',
      en: 'The support platform launched by the National Commission for anti-trafficking coordination.',
    },
  },
  '/faq': {
    title: { fr: `Questions fréquentes | ${SUFFIX.fr}`, ar: `الأسئلة الشائعة | ${SUFFIX.ar}`, en: `FAQ | ${SUFFIX.en}` },
    description: {
      fr: 'Confidentialité, gratuité du numéro vert 0800004747, démarches de signalement et protection des victimes.',
      ar: 'السرية ومجانية الرقم الأخضر 0800004747 ومسار التبليغ وحماية الضحايا.',
      en: 'Confidentiality, free green number 0800004747, reporting steps and victim protection.',
    },
  },
  '/espace-professionnels': {
    title: { fr: `Espace professionnels | ${SUFFIX.fr}`, ar: `فضاء المهنيين | ${SUFFIX.ar}`, en: `Professionals space | ${SUFFIX.en}` },
    description: {
      fr: 'Ressources, guides et orientation pour les professionnels de la protection des victimes.',
      ar: 'موارد وأدلة وتوجيه لمهنيي حماية الضحايا.',
      en: 'Resources, guides and referral help for victim protection professionals.',
    },
  },
  '/signalment': {
    title: { fr: `Signaler | ${SUFFIX.fr}`, ar: `التبليغ | ${SUFFIX.ar}`, en: `Report | ${SUFFIX.en}` },
    description: {
      fr: 'Signalez un cas de traite en toute confidentialité et soyez orienté vers les services adaptés.',
      ar: 'بلغوا عن حالة اتجار بالبشر بسرية تامة وسيتم توجيهكم نحو الخدمات المناسبة.',
      en: 'Report a trafficking case confidentially and get referred to the right services.',
    },
  },
  '/victim-confirmation': {
    title: { fr: `Demander de l'aide | ${SUFFIX.fr}`, ar: `طلب المساعدة | ${SUFFIX.ar}`, en: `Request help | ${SUFFIX.en}` },
    description: {
      fr: "Victime de traite ? Accédez à l'aide, à l'écoute et à la protection en toute confidentialité.",
      ar: 'هل أنتم ضحية اتجار بالبشر؟ استفيدوا من المساعدة والاستماع والحماية بسرية تامة.',
      en: 'Trafficking victim? Access confidential help, listening and protection.',
    },
  },
  '/request-help': {
    title: { fr: `Demande d'aide | ${SUFFIX.fr}`, ar: `طلب المساعدة | ${SUFFIX.ar}`, en: `Help request | ${SUFFIX.en}` },
    description: {
      fr: "Formulaire confidentiel de demande d'aide et d'orientation vers la protection.",
      ar: 'استمارة سرية لطلب المساعدة والتوجيه نحو الحماية.',
      en: 'Confidential help and referral request form.',
    },
  },
  '/victim-services-results': {
    title: { fr: `Services recommandés | ${SUFFIX.fr}`, ar: `الخدمات المقترحة | ${SUFFIX.ar}`, en: `Recommended services | ${SUFFIX.en}` },
    description: {
      fr: 'Services de protection recommandés selon votre situation et votre ville.',
      ar: 'خدمات الحماية المقترحة حسب وضعيتكم ومدينتكم.',
      en: 'Recommended protection services for your situation and city.',
    },
  },
};

const NOINDEX_PREFIXES = ['/admin', '/login', '/register', '/forgot-password', '/reset-password', '/verify-email', '/unauthorized', '/google-map'];

export function isNoIndexPath(pathname: string): boolean {
  if (typeof window !== 'undefined' && window.location.hostname === 'dash.antitraffickingmap.online') return true;
  return NOINDEX_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`) || pathname.startsWith('/admin'));
}

function upsertMeta(selector: string, create: () => HTMLElement): HTMLElement {
  const existing = document.head.querySelector(selector);
  if (existing) return existing as HTMLElement;
  const el = create();
  document.head.appendChild(el);
  return el;
}

function setMetaName(name: string, content: string): void {
  const el = upsertMeta(`meta[name="${name}"]`, () => {
    const m = document.createElement('meta');
    m.setAttribute('name', name);
    return m;
  }) as HTMLMetaElement;
  el.setAttribute('content', content);
}

function setMetaProperty(property: string, content: string): void {
  const el = upsertMeta(`meta[property="${property}"]`, () => {
    const m = document.createElement('meta');
    m.setAttribute('property', property);
    return m;
  }) as HTMLMetaElement;
  el.setAttribute('content', content);
}

function setLinkRel(rel: string, href: string, hreflang?: string): void {
  const selector = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]`;
  const el = upsertMeta(selector, () => {
    const l = document.createElement('link');
    l.setAttribute('rel', rel);
    if (hreflang) l.setAttribute('hreflang', hreflang);
    return l;
  }) as HTMLLinkElement;
  el.setAttribute('href', href);
}

function setJsonLd(id: string, data: unknown): void {
  let el = document.head.querySelector(`script[data-seo="${id}"]`) as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement('script');
    el.setAttribute('type', 'application/ld+json');
    el.setAttribute('data-seo', id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

export function applyRouteSeo(pathname: string, lang: string): void {
  if (typeof document === 'undefined') return;
  const site = getSiteUrl();
  const cleanPath = pathname === '/' ? '/' : pathname.replace(/\/$/, '');
  const baseKey = Object.keys(ROUTE_META).find((k) => cleanPath === k) ?? '/';
  // Dynamic actor pages reuse the services meta with a distinct canonical.
  const isActorPage = cleanPath.startsWith('/services/');
  const meta = ROUTE_META[isActorPage ? '/services' : baseKey] ?? ROUTE_META['/'];
  const l: SupportedLang = lang === 'ar' ? 'ar' : lang === 'en' ? 'en' : 'fr';
  const url = `${site}${cleanPath}`;
  const noindex = isNoIndexPath(cleanPath);

  document.title = meta.title[l];
  setMetaName('description', meta.description[l]);
  setMetaName('robots', noindex ? 'noindex, nofollow' : 'index, follow');
  setLinkRel('canonical', url);
  setMetaProperty('og:title', meta.title[l]);
  setMetaProperty('og:description', meta.description[l]);
  setMetaProperty('og:type', 'website');
  setMetaProperty('og:url', url);
  setMetaProperty('og:image', `${site}/welcome-cnclt.webp`);
  setMetaName('twitter:card', 'summary_large_image');
  setMetaName('twitter:title', meta.title[l]);
  setMetaName('twitter:description', meta.description[l]);
  setLinkRel('alternate', `${site}${cleanPath}?lang=fr`, 'fr');
  setLinkRel('alternate', `${site}${cleanPath}?lang=ar`, 'ar');
  setLinkRel('alternate', `${site}${cleanPath}?lang=en`, 'en');
  setLinkRel('alternate', url, 'x-default');

  // Breadcrumb schema on nested pages only.
  document.head.querySelectorAll('script[data-seo="breadcrumb"]').forEach((n) => n.remove());
  if (cleanPath !== '/' && !noindex) {
    const crumbs = cleanPath.split('/').filter(Boolean);
    setJsonLd('breadcrumb', {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: l === 'ar' ? 'الرئيسية' : l === 'en' ? 'Home' : 'Accueil', item: `${site}/` },
        ...crumbs.map((c, i) => ({
          '@type': 'ListItem',
          position: i + 2,
          name: decodeURIComponent(c),
          item: `${site}/${crumbs.slice(0, i + 1).join('/')}`,
        })),
      ],
    });
  }

  // FAQ schema on the FAQ page only.
  document.head.querySelectorAll('script[data-seo="faqpage"]').forEach((n) => n.remove());
  if (cleanPath === '/faq' && !noindex) {
    setJsonLd('faqpage', {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: l === 'ar' ? 'هل التبليغ سري ومجاني؟' : l === 'en' ? 'Is reporting confidential and free?' : 'Le signalement est-il confidentiel et gratuit ?',
          acceptedAnswer: {
            '@type': 'Answer',
            text:
              l === 'ar'
                ? 'نعم. الرقم الأخضر 0800004747 مجاني وسري.'
                : l === 'en'
                  ? 'Yes. The green number 0800004747 is free and confidential.'
                  : 'Oui. Le numéro vert 0800004747 est gratuit et confidentiel.',
          },
        },
      ],
    });
  }
}
